/* global axios */
import ApiClient from './ApiClient';

const getTimeOffset = () => -new Date().getTimezoneOffset() / 60;

// [PROSPEK] 2026-06-19 Send WhatsApp group exclusion only when the report toggle is enabled
const excludeWhatsappGroupsParam = excludeWhatsappGroups =>
  excludeWhatsappGroups ? { exclude_whatsapp_groups: true } : {};
// [/PROSPEK]

class ReportsAPI extends ApiClient {
  constructor() {
    super('reports', { accountScoped: true, apiVersion: 'v2' });
  }

  getReports({
    metric,
    from,
    to,
    type = 'account',
    id,
    groupBy,
    businessHours,
    // [PROSPEK] 2026-06-19 Add report-level WhatsApp group exclusion API support
    excludeWhatsappGroups,
    // [/PROSPEK]
  }) {
    return axios.get(`${this.url}`, {
      params: {
        metric,
        since: from,
        until: to,
        type,
        id,
        group_by: groupBy,
        business_hours: businessHours,
        timezone_offset: getTimeOffset(),
        // [PROSPEK] 2026-06-19 Add report-level WhatsApp group exclusion API support
        ...excludeWhatsappGroupsParam(excludeWhatsappGroups),
        // [/PROSPEK]
      },
    });
  }

  getDrilldown({
    metric,
    bucketTimestamp,
    from,
    to,
    type = 'account',
    id,
    groupBy,
    businessHours,
    page,
    perPage,
    signal,
    // [PROSPEK] 2026-09-29 Keep report-level WhatsApp group exclusion on the drilldown
    excludeWhatsappGroups,
    // [/PROSPEK]
  }) {
    const requestConfig = {
      params: {
        metric,
        bucket_timestamp: bucketTimestamp,
        since: from,
        until: to,
        type,
        id,
        group_by: groupBy,
        business_hours: businessHours,
        timezone_offset: getTimeOffset(),
        page,
        per_page: perPage,
        // [PROSPEK] 2026-09-29 Keep report-level WhatsApp group exclusion on the drilldown
        ...excludeWhatsappGroupsParam(excludeWhatsappGroups),
        // [/PROSPEK]
      },
    };

    if (signal) {
      requestConfig.signal = signal;
    }

    return axios.get(`${this.url}/drilldown`, requestConfig);
  }

  /* eslint-disable default-param-last */
  getSummary(
    since,
    until,
    type = 'account',
    id,
    groupBy,
    businessHours,
    // [PROSPEK] 2026-06-19 Add report-level WhatsApp group exclusion API support
    excludeWhatsappGroups
    // [/PROSPEK]
  ) {
    return axios.get(`${this.url}/summary`, {
      params: {
        since,
        until,
        type,
        id,
        group_by: groupBy,
        business_hours: businessHours,
        timezone_offset: getTimeOffset(),
        // [PROSPEK] 2026-06-19 Add report-level WhatsApp group exclusion API support
        ...excludeWhatsappGroupsParam(excludeWhatsappGroups),
        // [/PROSPEK]
      },
    });
  }
  /* eslint-enable default-param-last */

  getConversationMetric(type = 'account', page = 1) {
    return axios.get(`${this.url}/conversations`, {
      params: {
        type,
        page,
      },
    });
  }

  getAgentReports({
    from: since,
    to: until,
    businessHours,
    // [PROSPEK] 2026-06-19 Add report-level WhatsApp group exclusion API support
    excludeWhatsappGroups,
    // [/PROSPEK]
  }) {
    return axios.get(`${this.url}/agents`, {
      params: {
        since,
        until,
        business_hours: businessHours,
        // [PROSPEK] 2026-06-19 Add report-level WhatsApp group exclusion API support
        ...excludeWhatsappGroupsParam(excludeWhatsappGroups),
        // [/PROSPEK]
      },
    });
  }

  getConversationsSummaryReports({
    from: since,
    to: until,
    businessHours,
    // [PROSPEK] 2026-06-19 Add report-level WhatsApp group exclusion API support
    excludeWhatsappGroups,
    // [/PROSPEK]
  }) {
    return axios.get(`${this.url}/conversations_summary`, {
      params: {
        since,
        until,
        business_hours: businessHours,
        // [PROSPEK] 2026-06-19 Add report-level WhatsApp group exclusion API support
        ...excludeWhatsappGroupsParam(excludeWhatsappGroups),
        // [/PROSPEK]
      },
    });
  }

  getConversationTrafficCSV({
    daysBefore = 6,
    // [PROSPEK] 2026-06-20 Add WhatsApp group exclusion to conversation traffic CSV
    excludeWhatsappGroups,
    // [/PROSPEK]
  } = {}) {
    return axios.get(`${this.url}/conversation_traffic`, {
      params: {
        timezone_offset: getTimeOffset(),
        days_before: daysBefore,
        // [PROSPEK] 2026-06-20 Add WhatsApp group exclusion to conversation traffic CSV
        ...excludeWhatsappGroupsParam(excludeWhatsappGroups),
        // [/PROSPEK]
      },
    });
  }

  getLabelReports({
    from: since,
    to: until,
    businessHours,
    // [PROSPEK] 2026-06-19 Add report-level WhatsApp group exclusion API support
    excludeWhatsappGroups,
    // [/PROSPEK]
  }) {
    return axios.get(`${this.url}/labels`, {
      params: {
        since,
        until,
        business_hours: businessHours,
        // [PROSPEK] 2026-06-19 Add report-level WhatsApp group exclusion API support
        ...excludeWhatsappGroupsParam(excludeWhatsappGroups),
        // [/PROSPEK]
      },
    });
  }

  getInboxReports({
    from: since,
    to: until,
    businessHours,
    // [PROSPEK] 2026-06-19 Add report-level WhatsApp group exclusion API support
    excludeWhatsappGroups,
    // [/PROSPEK]
  }) {
    return axios.get(`${this.url}/inboxes`, {
      params: {
        since,
        until,
        business_hours: businessHours,
        // [PROSPEK] 2026-06-19 Add report-level WhatsApp group exclusion API support
        ...excludeWhatsappGroupsParam(excludeWhatsappGroups),
        // [/PROSPEK]
      },
    });
  }

  getTeamReports({
    from: since,
    to: until,
    businessHours,
    // [PROSPEK] 2026-06-19 Add report-level WhatsApp group exclusion API support
    excludeWhatsappGroups,
    // [/PROSPEK]
  }) {
    return axios.get(`${this.url}/teams`, {
      params: {
        since,
        until,
        business_hours: businessHours,
        // [PROSPEK] 2026-06-19 Add report-level WhatsApp group exclusion API support
        ...excludeWhatsappGroupsParam(excludeWhatsappGroups),
        // [/PROSPEK]
      },
    });
  }

  getBotMetrics({ from, to } = {}) {
    return axios.get(`${this.url}/bot_metrics`, {
      params: { since: from, until: to },
    });
  }

  getBotSummary({ from, to, groupBy, businessHours } = {}) {
    return axios.get(`${this.url}/bot_summary`, {
      params: {
        since: from,
        until: to,
        type: 'account',
        group_by: groupBy,
        business_hours: businessHours,
      },
    });
  }
}

export default new ReportsAPI();
